
// Constants
import { PAYMENT_TAGS } from './Payment'

// Models
import Model from './Model'

class Transaction extends Model {
  get details () {
    if (this.isFiscalTypeFee) {
      return this.formatDetails(
        TRANSACTIONS.FEE,
        this.sender,
        this.recipient
      )
    }

    if (this.isFiscalTypeCurrencyConversion) {
      return this.formatDetails(
        TRANSACTIONS.CONVERT,
        this.sender,
        this.recipient
      )
    }

    if (this.isIncoming && this.hasExternalAccount) {
      return this.formatDetails(
        TRANSACTIONS.TOPUP,
        this.externalAccount,
        this.recipient
      )
    }

    if (this.isOutgoing && this.hasExternalAccount) {
      return this.formatDetails(
        TRANSACTIONS.WITHDRAWAL,
        this.sender,
        this.externalAccount
      )
    }

    if (this.isIncoming || (this.isOutgoing && !this.hasExternalAccount)) {
      return this.formatDetails(
        TRANSACTIONS.INTERNAL,
        this.sender,
        this.recipient
      )
    }

    return this.formatDetails()
  }

  get name () {
    return this.details.transaction
  }

  get isInitiated () {
    return this.status === TRANSACTION_STATUSES.INITIATED
  }

  get isIncoming () {
    return this.type === TRANSACTION_TYPES.INCOMING
  }

  get isOutgoing () {
    return this.type === TRANSACTION_TYPES.OUTGOING
  }

  get isPending () {
    return this.status === TRANSACTION_STATUSES.PENDING
  }

  get isAwaiting () {
    return this.status === TRANSACTION_STATUSES.INITIATED ||
      this.status === TRANSACTION_STATUSES.PENDING
  }

  get isOnHold () {
    return this.status === TRANSACTION_STATUSES.ON_HOLD
  }

  get isDone () {
    return this.status === TRANSACTION_STATUSES.DONE
  }

  get isCanceled () {
    return this.status === TRANSACTION_STATUSES.CANCELED
  }

  get isProcessing () {
    return this.status === TRANSACTION_STATUSES.PROCESSING
  }

  get isProcessingComplete () {
    return this.status === TRANSACTION_STATUSES.PROCESSING_COMPLETE
  }

  get isPendingCancellation () {
    return this.status === TRANSACTION_STATUSES.PROCESSING_CANCELLATION
  }

  get isProcessingFailed () {
    return this.status === TRANSACTION_STATUSES.PROCESSING_FAILED
  }

  get isOnVerifying () {
    return this.status === TRANSACTION_STATUSES.VERIFICATION
  }

  get isTopup () {
    return this.name === TRANSACTIONS.TOPUP
  }

  get isConvert () {
    return this.name === TRANSACTIONS.CONVERT
  }

  get isWithdrawal () {
    return this.name === TRANSACTIONS.WITHDRAWAL
  }

  get isInternal () {
    return this.name === TRANSACTIONS.INTERNAL
  }

  get isFee () {
    return this.name === TRANSACTIONS.FEE
  }

  get isRecurringFee () {
    return this.recurringFee === true
  }

  get isSafeguardingMoneyMovement () {
    return this.paymentTags.includes(PAYMENT_TAGS.SAFEGUARDING_MONEY_MOVEMENT)
  }

  get isFiscalTypeBase () {
    return this.fiscalType === FISCAL_TYPES.BASE
  }

  get isFiscalTypeFee () {
    return this.fiscalType === FISCAL_TYPES.FEE
  }

  get isFiscalTypeCurrencyConversion () {
    return this.fiscalType === FISCAL_TYPES.CURRENCY_CONVERSION
  }

  get isInvalidTopup () {
    return this.isTopup &&
    (!this.recipientProfile || !this.recipientProfile.id)
  }

  get isNonSepaTopup () {
    return this.isTopup && this.sepa === false
  }

  get isWaitingSepaReturn () {
    return this.sepaStatus === SEPA_STATUSES.WAITING_FOR_RETURN
  }

  get isSepaCancelling () {
    return this.sepaStatus === SEPA_STATUSES.CANCELLING
  }

  get isSepaCancellationFailed () {
    return this.sepaStatus === SEPA_STATUSES.CANCELLATION_FAILED
  }

  get waitingForOpeningDay () {
    return (this.isPending || this.isProcessingComplete) &&
      this.isFiscalTypeBase
  }

  get isAutomaticConversion () {
    return this.paymentTags.includes(PAYMENT_TAGS.AUTOMATIC) &&
      this.fiscalType === FISCAL_TYPES.CURRENCY_CONVERSION
  }

  get isSpecialRate () {
    return this.paymentTags.includes(PAYMENT_TAGS.SPECIAL_RATE)
  }

  get isReturn () {
    return this.return === true
  }

  get isReturned () {
    return this.returned === true
  }

  get hasDebt () {
    return this.coverage && this.debt > 0
  }

  get hasDebtCovered () {
    return this.coverage && this.debt === 0
  }

  get hasExternalAccount () {
    return this.externalAccount != null
  }

  get hasVipStatusPriority () {
    if (this.senderProfile != null && this.senderProfile.vip === true) {
      return true
    }

    if (this.recipientProfile != null && this.recipientProfile.vip === true) {
      return true
    }

    return false
  }

  get senderProfile () {
    return this.details.sender.client
  }

  get recipientProfile () {
    return this.details.recipient.client
  }

  get senderName () {
    const name = (this.senderProfile != null)
      ? this.senderProfile.name
      : this.details.sender.name

    return name != null ? name : ''
  }

  get recipientName () {
    const name = (this.recipientProfile != null)
      ? this.recipientProfile.name
      : this.details.recipient.name

    return name != null ? name : ''
  }

  get onboarder () {
    if (this.recipientProfile != null && this.isIncoming) {
      return this.recipientProfile.onboarderName
    }

    if (this.senderProfile != null && this.isOutgoing) {
      return this.senderProfile.onboarderName
    }
  }

  static get typeOptions () {
    return [{
      name: 'Top-up',
      value: TRANSACTIONS.TOPUP
    }, {
      name: 'Internal',
      value: TRANSACTIONS.INTERNAL
    }, {
      name: 'Withdrawal',
      value: TRANSACTIONS.WITHDRAWAL
    }, {
      name: 'Fee',
      value: TRANSACTIONS.FEE
    }, {
      name: 'Convert',
      value: TRANSACTIONS.CONVERT
    }]
  }

  static get partyOptions () {
    return [
      { name: 'Sender', value: TRANSACTION_PARTY_TYPES.SENDER },
      { name: 'Recipient', value: TRANSACTION_PARTY_TYPES.RECIPIENT }
    ]
  }

  static get urgencyOptions () {
    return [
      { name: 'Standard', value: URGENCY_TYPES.URGENT },
      { name: 'Express', value: URGENCY_TYPES.EXPRESS }
    ]
  }

  static get typesByFeeOptions () {
    return [
      { name: 'Top-up', value: TRANSACTION_TYPES_BY_FEE.INCOMING_EXTERNAL },
      { name: 'Internal (between own accounts)', value: TRANSACTION_TYPES_BY_FEE.OUTGOING_WITHIN_PROFILE },
      { name: 'Internal (between two clients)', value: TRANSACTION_TYPES_BY_FEE.OUTGOING_INTERNAL },
      { name: 'Withdrawal', value: TRANSACTION_TYPES_BY_FEE.OUTGOING_EXTERNAL },
      { name: 'Conversion (CC)', value: TRANSACTION_TYPES_BY_FEE.CURRENCY_CONVERSION },
      { name: 'Forex', value: TRANSACTION_TYPES_BY_FEE.FOREX_CONVERSION },
      { name: 'Decta markup (outgoing)', value: TRANSACTION_TYPES_BY_FEE.OUTGOING_DECTA },
      { name: 'Decta markup (incoming 206, 225, 227)', value: TRANSACTION_TYPES_BY_FEE.INCOMING_DECTA },
      { name: 'Integrator (incoming)', value: TRANSACTION_TYPES_BY_FEE.INCOMING_INTEGRATOR }
    ]
  }

  static get directionOptions () {
    return [
      { name: 'Incoming', value: TRANSACTION_TYPES.INCOMING },
      { name: 'Outgoing', value: TRANSACTION_TYPES.OUTGOING }
    ]
  }

  static get sepaOptions () {
    return [
      { name: 'SEPA', value: true },
      { name: 'Non SEPA', value: false }
    ]
  }

  static get statusOptions () {
    return [
      { name: 'Initiated', value: TRANSACTION_STATUSES.INITIATED },
      { name: 'On hold', value: TRANSACTION_STATUSES.ON_HOLD },
      { name: 'Pending', value: TRANSACTION_STATUSES.PENDING },
      { name: 'Verification', value: TRANSACTION_STATUSES.VERIFICATION },
      { name: 'Processing', value: TRANSACTION_STATUSES.PROCESSING },
      { name: 'Processing complete', value: TRANSACTION_STATUSES.PROCESSING_COMPLETE },
      { name: 'Processing failed', value: TRANSACTION_STATUSES.PROCESSING_FAILED },
      { name: 'Done', value: TRANSACTION_STATUSES.DONE },
      { name: 'Canceled', value: TRANSACTION_STATUSES.CANCELED }
    ]
  }

  get schema () {
    return [
      'admin',
      'adminComment',
      'amount',
      'channel',
      'comment',
      'createdAt',
      'currency',
      'currencyRates',
      'externalAccount',
      'externalId',
      'fee',
      'feeType',
      'finishedAt',
      'fiscalType',
      'hidden',
      'id',
      'manualFee',
      'offset',
      'recipient',
      'referenceNumber',
      'return',
      'returned',
      'sender',
      'sepa',
      'sepaExternalAccount',
      'sepaReason',
      'sepaStatus',
      'status',
      'type',
      'updatedAt',
      'valueDateAt',
      'urgency',
      'authorId',
      'authorRole',
      'updatedById',
      'updatedByRole',
      'paymentTags',
      'paymentId',
      'paymentStatus',
      'coverage',
      'debt',
      'settledAt',
      'recurringFee'
    ]
  }

  formatDetails (transaction = '', sender, recipient) {
    return {
      transaction,
      sender: sender || {},
      recipient: recipient || {}
    }
  }
}

export const TRANSACTIONS = {
  TOPUP: 'TOPUP',
  INTERNAL: 'INTERNAL',
  WITHDRAWAL: 'WITHDRAWAL',
  FEE: 'FEE',
  CONVERT: 'CONVERT'
}

export const TRANSACTION_TYPES = {
  INCOMING: 'INCOMING',
  OUTGOING: 'OUTGOING'
}

export const FISCAL_TYPES = {
  BASE: 'BASE',
  FEE: 'FEE',
  CURRENCY_CONVERSION: 'CURRENCY_CONVERSION'
}

export const TRANSACTION_PARTY_TYPES = {
  SENDER: 'sender',
  RECIPIENT: 'recipient'
}

export const URGENCY_TYPES = {
  URGENT: 'URGENT',
  EXPRESS: 'EXPRESS'
}

export const TRANSACTION_STATUSES = {
  INITIATED: 'INITIATED',
  ON_HOLD: 'ON_HOLD',
  PENDING: 'PENDING',
  DONE: 'DONE',
  CANCELED: 'CANCELED',
  PROCESSING: 'PROCESSING',
  PROCESSING_COMPLETE: 'PROCESSING_COMPLETE',
  PROCESSING_FAILED: 'PROCESSING_FAILED',
  VERIFICATION: 'VERIFICATION'
}

export const SEPA_STATUSES = {
  DONE: 'DONE',
  REJECTED: 'REJECTED',
  RETRYING: 'RETRYING',
  WAITING_FOR_RETURN: 'WAITING_FOR_RETURN',
  CANCELED: 'CANCELED',
  CANCELLING: 'CANCELLING',
  CANCELLATION_FAILED: 'CANCELLATION_FAILED',
  ERROR: 'ERROR'
}

export const TRANSACTION_TYPES_BY_FEE = {
  INCOMING_EXTERNAL: 'INCOMING_EXTERNAL',
  OUTGOING_WITHIN_PROFILE: 'OUTGOING_WITHIN_PROFILE',
  OUTGOING_INTERNAL: 'OUTGOING_INTERNAL',
  OUTGOING_EXTERNAL: 'OUTGOING_EXTERNAL',
  CURRENCY_CONVERSION: 'CURRENCY_CONVERSION',
  FOREX_CONVERSION: 'FOREX_CONVERSION',
  OUTGOING_DECTA: 'OUTGOING_DECTA',
  INCOMING_DECTA: 'INCOMING_DECTA',
  INCOMING_INTEGRATOR: 'INCOMING_INTEGRATOR'
}

export const OPERATION_GROUPS = {
  ON_HOLD: 'ON_HOLD',
  MANUAL: 'MANUAL',
  VERIFICATION: 'VERIFICATION',
  TOPUP_VERIFICATION: 'TOPUP_VERIFICATION',
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  ERRORS: 'ERRORS'
}

export default Transaction
